import React, { CSSProperties } from 'react';
import Icon, { IconType } from '../icon';
import { useIntl } from 'react-intl';
import translations from './indentation.i18n';
import {
  MuligeStudiestedDto,
  MuligeStudiestedDtoTypeDtoEnum,
} from '../../api/generated/studiested-generated-api';
import { MuligPraksisstedDto } from '../../api/generated/praksissted-generated-api';

/**
 * Felleskomponent for å indentere både studiesteder og praksissteder ved opplisting
 */
const IndentMuligSted: React.FC<
  React.PropsWithChildren<{
    sted: {
      id: string;
      navn: JSX.Element;
      deaktivert?: boolean;
      dybde?: number;
      endenode?: boolean;
    };
    multiple?: boolean;
  }>
> = ({ sted, multiple }) => {
  const { formatMessage } = useIntl();
  const disabledSuffix = formatMessage({
    id: translations['manglerTilganger'].id,
  });

  const style: CSSProperties = {
    paddingLeft: `${1 + (sted.dybde || 0)}rem`,
    display: `${multiple ? 'inline' : 'block'}`,
  };

  if (sted.dybde === 0) {
    style['fontWeight'] = 600;
  }

  return (
    <div style={style}>
      {sted.endenode && (
        <>
          <Icon type={IconType.EndNode} htmlId={sted.id} />
          {'\t'}
        </>
      )}
      {sted.deaktivert ? (
        <>
          {sted.navn} {disabledSuffix}
        </>
      ) : (
        <>{sted.navn}</>
      )}
    </div>
  );
};

export const IndentMuligPraksissted: React.FC<
  React.PropsWithChildren<{
    praksissted: MuligPraksisstedDto;
    multiple?: boolean;
  }>
> = ({ praksissted, multiple }) => (
  <IndentMuligSted
    sted={{ ...praksissted, navn: <>{praksissted.navn}</> }}
    multiple={multiple}
  />
);

export const getMuligStudiestedDybde = (studiested: MuligeStudiestedDto) => {
  switch (studiested.type) {
    case MuligeStudiestedDtoTypeDtoEnum.Institusjon:
      return 0;
    case MuligeStudiestedDtoTypeDtoEnum.Fakultet:
      return 1;
    case MuligeStudiestedDtoTypeDtoEnum.Institutt:
      return 2;
    case MuligeStudiestedDtoTypeDtoEnum.Gruppe:
      return 3;
  }
};

const getMuligStudiestedEndenode = (studiested: MuligeStudiestedDto) =>
  studiested.type === MuligeStudiestedDtoTypeDtoEnum.Gruppe;

export const IndentMuligStudiested: React.FC<
  React.PropsWithChildren<{
    studiested: MuligeStudiestedDto;
  }>
> = ({ studiested }) => {
  const dybde = getMuligStudiestedDybde(studiested);
  const endenode = getMuligStudiestedEndenode(studiested);

  return (
    <IndentMuligSted
      sted={{ ...studiested, navn: <>{studiested.navn}</>, dybde, endenode }}
    />
  );
};
